// TODO: move to order/importer/ — hooks and DTOs are now co-located in order/importer/
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { LuppApi } from '../../lupp.service';
import { IMPORTER_API_BASE_PATH, LUPP_QUERY_KEYS } from '../../constants.lupp';
import { downloadFileFromFilehub } from '../../fileUpload/fileUpload';
import { FILEHUB_MODULE_NAME } from '../../../../../constants/constants';
import type { ModuleFileReference } from '../dto.order';
import type {
  ImporterOrder,
  ImporterOrderDetail,
  VehicleOrder,
  VehicleOrderCheckoutDetail,
  LineItemDetail,
  DeliveryItem,
  ChangeRequestDetail,
  OrderStatusEvent,
  OrderStatusEventType,
  CreateImporterOrderRequest,
  UpdateOrderRequest,
  VehicleOrderLineRequest,
  OrderDelivery,
  DeliveryStatus,
  UpdateDeliveryStatusRequest,
  ConfirmPickupPayload,
  OrderNotificationsResult,
  VehicleOrderComparison,
} from '../importer/dto.importer';
import { getPortalStoreState } from '@lucid/portal-core';

// Allow routing importer traffic to a different host (e.g. local x-importer dev server).
// Falls back to the standard LUPP base URL when not set.
const IMPORTER_BASE_URL = import.meta.env.VITE_IMPORTER_BASE_URL as string | undefined;
const CART_TENANT = import.meta.env.VITE_CART_TENANT as string | undefined;
const importerArgs = IMPORTER_BASE_URL
  ? ([{ axiosConfig: { baseURL: IMPORTER_BASE_URL } }] as const)
  : ([] as const);

/*
|--------------------------------------------------------------------------
| Internal Java DTO shapes (x-importer serialisation)
| These are the actual JSON shapes returned by x-importer BFF endpoints.
| LuppApi unwraps the outer ApiResponsePayload envelope, so these types
| represent the `data` field of each response.
|--------------------------------------------------------------------------
*/

type OrderDto = {
  orderId: string;
  orderNumber: string;
  importerId?: string | null;
  status?: string | null;
  version?: number | null;
  countryCode?: string | null;
  locale?: string | null;
  requestedQty?: number | null;
  allocatedQty?: number | null;
  approvedQty?: number | null;
  autoApprovedQty?: number | null;
  pendingQty?: number | null;
  orderTotal?: number | null;
  vehicles?: {
    vehicleOrderNumber?: string;
    orderGroupNumber?: string;
    type?: string;
    status?: string;
    shortcode?: string;
    model?: string;
    vehicleId?: string;
    vin?: string;
    notes?: string | null;
  }[];
  createdAt?: string | null;
  updatedAt?: string | null;
  moduleFileReferenceDtoList?: ModuleFileReference[];
};

type PagedOrdersDto = {
  items: OrderDto[];
  metadata: { pagingCriteria: PagingMetadata; statusGroupCounts?: Record<string, number> };
};

type LineItemDto = {
  lineItemId: string;
  orderId: string;
  model?: string | null;
  trim?: string | null;
  modelYear?: string | null;
  vin?: string | null;
  statusName?: string | null;
  allocationStatus?: string | null;
  vehicleStatus?: string | null;
  workflowType?: string | null;
  orderPreference?: string | null;
  bucketId?: string | null;
  changeRequestId?: string | null;
  vehicleOrderNumber?: string | null;
  importerOrderNumber?: string | null;
  assetLocation?: string | null;
  lineTotalAmount?: number | null;
  currency?: string | null;
  notes?: string | null;
  eta?: {
    currentStatusKey?: string | null;
    currentStatusLabel?: string | null;
    remainingDays?: number | null;
    displayText?: string | null;
    estimatedDeliveryDate?: string | null;
    windowStartDate?: string | null;
    windowEndDate?: string | null;
    lastComputedAt?: string | null;
    source?: string | null;
  } | null;
};

type PagedLineItemsDto = {
  items: LineItemDto[];
  metadata: { pagingCriteria: PagingMetadata; statusGroupCounts?: Record<string, number> };
};

type DeliveryDtoItem = {
  lineItemId: string;
  orderId: string;
  importerId?: string | null;
  model?: string | null;
  vin?: string | null;
  bucketId?: string | null;
  allocationStatus?: string | null;
  vehicleStatus?: string | null;
  attributes?: Record<string, string> | null;
  updatedAt?: string | null;
};

type LineItemActionResultDto = {
  lineItemId: string;
  orderId?: string | null;
  allocationStatus?: string | null;
  statusName?: string | null;
  message?: string | null;
};

type BulkApproveResponseDto = {
  totalRequested: number;
  approved: number;
  failed: number;
  results: { lineItemId: string; success: boolean; message?: string | null }[];
};

type VehicleOrderDto = {
  vehicleOrderId: string;
  vehicleOrderNumber: string;
  importerOrderNumber?: string | null;
  vin?: string | null;
  status?: string | null;
  vehicleJourneyStatus?: string | null;
  scheduledDeliveryDate?: string | null;
  customerHandOffDate?: string | null;
  orderPreference?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  checkoutDetail?: VehicleOrderCheckoutDetail | null;
  notes?: string | null;
  eta?: {
    currentStatusKey?: string | null;
    currentStatusLabel?: string | null;
    remainingDays?: number | null;
    displayText?: string | null;
    estimatedDeliveryDate?: string | null;
    windowStartDate?: string | null;
    windowEndDate?: string | null;
    lastComputedAt?: string | null;
    source?: string | null;
  } | null;
};

type OrderEventsDtoResult = {
  orderId: string;
  events: {
    eventId: string;
    entityType?: string | null;
    entityId?: string | null;
    orderId?: string | null;
    eventType: string;
    eventData?: Record<string, unknown> | null;
    timestamp: string;
  }[];
};

type PagingMetadata = {
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
};

type SubmitCartResult = {
  order: OrderDto;
  lineItems: LineItemDto[];
};

type CartItemAttributeDto = {
  attributeName?: string | null;
  attributeValue?: string | null;
};

type CartItemDto = {
  itemId?: string | null;
  itemUISequenceNumber?: number | null;
  itemCategory?: string | null;
  itemName?: string | null;
  itemCode?: string | null;
  itemDescription?: string | null;
  unitOfMeasurement?: string | null;
  minimumOrderQuantity?: number | null;
  maximumSellingRetailPrice?: number | null;
  quantityOrdered?: number | null;
  lineItemBasePrice?: number | null;
  unitPrice?: number | null;
  lineTotalAmount?: number | null;
  isItemAvailable?: boolean | null;
  lineItemStatus?: string | null;
  itemContext?: Record<string, unknown> | null;
  itemAttributes?: CartItemAttributeDto[] | null;
};

type CartPricingSummaryDto = {
  subtotal?: number | null;
  discountTotal?: number | null;
  shippingTotal?: number | null;
  feeTotal?: number | null;
  taxTotal?: number | null;
  grandTotal?: number | null;
  currencyCode?: string | null;
  availableCreditLineTotal?: number | null;
  remainingAfterPurchase?: number | null;
};

export type CheckoutCartDto = {
  cartId?: string | null;
  cartStatus?: string | null;
  cartName?: string | null;
  cartUIDisplayIdentifier?: string | null;
  countryCode?: string | null;
  currencyCode?: string | null;
  items?: CartItemDto[] | null;
  lineItemCount?: number | null;
  itemCount?: number | null;
  pricingSummary?: CartPricingSummaryDto | null;
};

type CheckoutCartResult = {
  cart: CheckoutCartDto;
  /** True when Lucid has paused new order placement for this importer. */
  ordersPaused?: boolean;
  /** Reason Lucid supplied for pausing this importer's ordering (nullable). */
  ordersPausedReason?: string | null;
};

/*
|--------------------------------------------------------------------------
| Mapping helpers — Java DTO → FE type
|--------------------------------------------------------------------------
*/

const mapOrderDto = (dto: OrderDto): ImporterOrder => ({
  importerOrderId: dto.orderId,
  importerOrderNumber: dto.orderNumber,
  statusName: dto.status ?? null,
  totalVins: dto.requestedQty ?? null,
  totalAmount: dto.orderTotal ?? null,
  location: null,
  notes: null,
  orderDate: dto.createdAt ?? null,
  updatedAt: dto.updatedAt ?? null,
});

const mapVehicleOrderDto = (
  dto: VehicleOrderDto,
  importerOrderId?: string | null
): VehicleOrder => ({
  orderIdentifier: dto.vehicleOrderId,
  orderNumber: dto.vehicleOrderNumber,
  importerOrderNumber: dto.importerOrderNumber ?? null,
  importerOrderId: importerOrderId ?? null,
  vin: dto.vin ?? null,
  statusName: dto.status ?? null,
  vehicleJourneyStatus: dto.vehicleJourneyStatus ?? null,
  orderDate: dto.createdAt ?? null,
  scheduledDeliveryDate: dto.scheduledDeliveryDate ?? null,
  handoffDate: dto.customerHandOffDate ?? null,
  totalAmount: dto.checkoutDetail?.checkoutInfo?.pricing?.total?.value ?? null,
  location: dto.checkoutDetail?.vehicleDetails?.address
    ? [
        dto.checkoutDetail.vehicleDetails.address.city,
        dto.checkoutDetail.vehicleDetails.address.country,
      ]
        .filter(Boolean)
        .join(', ') || null
    : null,
  checkoutDetail: dto.checkoutDetail ?? null,
  notes: dto.notes ?? null,
  orderPreference: dto.orderPreference ?? null,
  eta: dto.eta ?? null,
});

const mapLineItemDto = (dto: LineItemDto, parentOrderId: string | null): VehicleOrder => ({
  orderIdentifier: dto.lineItemId,
  importerOrderId: parentOrderId ?? dto.orderId,
  importerOrderNumber: dto.importerOrderNumber ?? null,
  vin: dto.vin ?? null,
  statusName: dto.statusName ?? null,
  orderNumber: dto.vehicleOrderNumber ?? null,
  model: dto.model ?? null,
  trim: dto.trim ?? null,
  modelYear: dto.modelYear ?? null,
  vehicleStatus: dto.vehicleStatus ?? null,
  customerName: null,
  totalAmount: dto.lineTotalAmount ?? null,
  location: dto.assetLocation ?? null,
  notes: dto.notes ?? null,
  orderPreference: dto.orderPreference ?? null,
  eta: dto.eta ?? null,
});

/*
|--------------------------------------------------------------------------
| Search order request body
|--------------------------------------------------------------------------
*/

type SearchOrdersBody = {
  importerOrganizationId?: string;
  statuses?: string[];
  searchCriteria?: {
    criteria: { searchText: string; matchType: 'contains' | 'exact' }[];
  };
  pagingCriteria?: { pageNumber: number; pageSize: number };
  sortingCriteria?: { sortFieldName: string; sortDirection: 'ASC' | 'DESC' };
};

type SearchLineItemsBody = {
  importerOrganizationId?: string;
  statusFilter?: string[];
  pagingCriteria?: { pageNumber: number; pageSize: number };
  sortingCriteria?: { sortFieldName: string; sortDirection: 'ASC' | 'DESC' };
  searchCriteria?: { criteria: { searchText: string; matchType: 'contains' | 'exact' }[] };
};

/*
|--------------------------------------------------------------------------
| Search importer orders  POST importer/X/v1/orders/searches
|--------------------------------------------------------------------------
*/

export type UseSearchImporterOrdersParams = {
  page?: number;
  size?: number;
  searchString?: string;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  statuses?: string[];
  enabled?: boolean;
};

export const useSearchImporterOrders = ({
  page = 0,
  size = 10,
  searchString,
  sortField,
  sortOrder = 'desc',
  statuses,
  enabled = true,
}: UseSearchImporterOrdersParams = {}) => {
  return useQuery({
    queryKey: [
      LUPP_QUERY_KEYS.IMPORTER_ORDERS,
      page,
      size,
      searchString,
      sortField,
      sortOrder,
      statuses,
    ],
    queryFn: async () => {
      const importerOrganizationId = getPortalStoreState().userIdentity?.businessOrganizationId;

      const body: SearchOrdersBody = {
        importerOrganizationId,
        pagingCriteria: { pageNumber: page, pageSize: size },
      };

      if (searchString) {
        body.searchCriteria = {
          criteria: [{ searchText: searchString, matchType: 'contains' }],
        };
      }

      if (sortField) {
        // 'orderDate' is a FE display alias for the backend field 'createdAt'
        const backendSortField = sortField === 'orderDate' ? 'createdAt' : sortField;
        body.sortingCriteria = {
          sortFieldName: backendSortField,
          sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC',
        };
      }

      if (statuses && statuses.length > 0) {
        body.statuses = statuses;
      }

      const response = await LuppApi.post<SearchOrdersBody, PagedOrdersDto>(
        `${IMPORTER_API_BASE_PATH}/orders/searches`,
        body,
        ...importerArgs
      );

      return {
        items: (response.items ?? []).map(mapOrderDto),
        totalCount: response.metadata?.pagingCriteria?.totalRecords ?? 0,
        totalPages: response.metadata?.pagingCriteria?.totalPages ?? 0,
        currentPage: response.metadata?.pagingCriteria?.pageNumber ?? page,
        statusGroupCounts: response.metadata?.statusGroupCounts ?? {},
      };
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};

/*
|--------------------------------------------------------------------------
| Search vehicle orders  POST importer/X/v1/orders/{importerOrderId}/line-items/searches
|--------------------------------------------------------------------------
*/

export type UseSearchVehicleOrdersParams = {
  importerOrderId: string | null;
  page?: number;
  size?: number;
  searchString?: string;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  statusFilter?: string[];
  enabled?: boolean;
};

export const useSearchVehicleOrders = ({
  importerOrderId,
  page = 0,
  size = 10,
  searchString,
  sortField,
  sortOrder = 'desc',
  statusFilter,
  enabled = true,
}: UseSearchVehicleOrdersParams) => {
  return useQuery({
    queryKey: [
      LUPP_QUERY_KEYS.IMPORTER_ORDER_LINE_ITEMS,
      importerOrderId,
      page,
      size,
      searchString,
      sortField,
      sortOrder,
      statusFilter,
    ],
    queryFn: async () => {
      const importerOrganizationId = getPortalStoreState().userIdentity?.businessOrganizationId;

      const body: SearchLineItemsBody = {
        importerOrganizationId,
        pagingCriteria: { pageNumber: page, pageSize: size },
      };

      if (statusFilter && statusFilter.length > 0) {
        body.statusFilter = statusFilter;
      }

      if (searchString) {
        body.searchCriteria = {
          criteria: [{ searchText: searchString, matchType: 'contains' }],
        };
      }

      if (sortField) {
        body.sortingCriteria = {
          sortFieldName: sortField,
          sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC',
        };
      }

      const response = await LuppApi.post<SearchLineItemsBody, PagedLineItemsDto>(
        `${IMPORTER_API_BASE_PATH}/orders/${importerOrderId}/line-items/searches`,
        body,
        ...importerArgs
      );

      return {
        items: (response.items ?? []).map((item) => mapLineItemDto(item, importerOrderId)),
        totalCount: response.metadata?.pagingCriteria?.totalRecords ?? 0,
        totalPages: response.metadata?.pagingCriteria?.totalPages ?? 0,
        currentPage: response.metadata?.pagingCriteria?.pageNumber ?? page,
        statusGroupCounts: response.metadata?.statusGroupCounts ?? {},
      };
    },
    enabled: enabled && !!importerOrderId,
    placeholderData: keepPreviousData,
  });
};

/*
|--------------------------------------------------------------------------
| Create importer order  POST importer/X/v1/orders
|--------------------------------------------------------------------------
*/

export const useCreateImporterOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateImporterOrderRequest) => {
      // LuppApi unwraps ApiResponsePayload — response IS the OrderDto
      return await LuppApi.post<CreateImporterOrderRequest, OrderDto>(
        `${IMPORTER_API_BASE_PATH}/orders`,
        body,
        ...importerArgs
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.IMPORTER_ORDERS] });
    },
  });
};

/*
|--------------------------------------------------------------------------
| Get importer order detail  GET importer/X/v1/orders/{importerOrderId}
|--------------------------------------------------------------------------
*/

export type UseGetImporterOrderParams = {
  importerOrderId: string | null;
  enabled?: boolean;
};

export const useGetImporterOrder = ({
  importerOrderId,
  enabled = true,
}: UseGetImporterOrderParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.IMPORTER_ORDER_DETAIL, importerOrderId],
    queryFn: async () => {
      // LuppApi unwraps ApiResponsePayload — response IS the OrderDto
      const response = await LuppApi.get<OrderDto>(
        `${IMPORTER_API_BASE_PATH}/orders/${importerOrderId}`,
        ...importerArgs
      );
      return {
        ...mapOrderDto(response),
        orderDate: response.createdAt ?? null,
        moduleFileReferenceDtoList: response.moduleFileReferenceDtoList ?? [],
      } as ImporterOrderDetail;
    },
    enabled: enabled && !!importerOrderId,
  });
};

/*
|--------------------------------------------------------------------------
| Patch importer order  PATCH importer/X/v1/orders/{importerOrderId}
|--------------------------------------------------------------------------
*/

export type PatchImporterOrderParams = {
  importerOrderId: string;
  body: UpdateOrderRequest;
};

export const usePatchImporterOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ importerOrderId, body }: PatchImporterOrderParams) => {
      // LuppApi unwraps ApiResponsePayload — response IS UpdateOrderResponse
      return await LuppApi.patch<
        UpdateOrderRequest,
        { importerOrderNumber: string; status: string; version: number; updatedAt: string }
      >(`${IMPORTER_API_BASE_PATH}/orders/${importerOrderId}`, body, ...importerArgs);
    },
    onSuccess: (_data, { importerOrderId }) => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.IMPORTER_ORDERS] });
      queryClient.invalidateQueries({
        queryKey: [LUPP_QUERY_KEYS.IMPORTER_ORDER_DETAIL, importerOrderId],
      });
    },
  });
};

/*
|--------------------------------------------------------------------------
| Poll order events  GET importer/X/v1/orders/{importerOrderId}/events
|--------------------------------------------------------------------------
*/

export type UseGetOrderEventsParams = {
  importerOrderId: string | null;
  since?: string;
  enabled?: boolean;
};

export const useGetOrderEvents = ({
  importerOrderId,
  since,
  enabled = true,
}: UseGetOrderEventsParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.IMPORTER_ORDER_EVENTS, importerOrderId, since],
    queryFn: async () => {
      const url = since
        ? `${IMPORTER_API_BASE_PATH}/orders/${importerOrderId}/events?since=${encodeURIComponent(since)}`
        : `${IMPORTER_API_BASE_PATH}/orders/${importerOrderId}/events`;
      // LuppApi unwraps ApiResponsePayload — response IS OrderEventsDto: { orderId, events[] }
      const response = await LuppApi.get<OrderEventsDtoResult>(url, ...importerArgs);
      return (response.events ?? []).map(
        (e): OrderStatusEvent => ({
          eventId: e.eventId,
          eventType: e.eventType as OrderStatusEventType,
          timestamp: e.timestamp,
          lineItemId: (e.eventData?.lineItemId as string) ?? null,
          detail: (e.eventData?.message as string) ?? null,
        })
      );
    },
    enabled: enabled && !!importerOrderId,
    refetchInterval: enabled && !!importerOrderId ? 30_000 : false,
  });
};

/*
|--------------------------------------------------------------------------
| Get single line item  GET importer/X/v1/orders/{importerOrderId}/line-items/{lineItemId}
|--------------------------------------------------------------------------
*/

export type UseGetImporterOrderLineItemParams = {
  importerOrderId: string | null;
  lineItemId: string | null;
  enabled?: boolean;
};

export const useGetImporterOrderLineItem = ({
  importerOrderId,
  lineItemId,
  enabled = true,
}: UseGetImporterOrderLineItemParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.IMPORTER_ORDER_LINE_ITEM, importerOrderId, lineItemId],
    queryFn: async () => {
      const response = await LuppApi.get<LineItemDetail>(
        `${IMPORTER_API_BASE_PATH}/orders/${importerOrderId}/line-items/${lineItemId}`,
        ...importerArgs
      );
      return response;
    },
    enabled: enabled && !!importerOrderId && !!lineItemId,
  });
};

/*
|--------------------------------------------------------------------------
| Get vehicle order  GET importer/X/v1/vehicle-orders/{vehicleOrderNumber}
|--------------------------------------------------------------------------
*/

export type UseGetVehicleOrderParams = {
  vehicleOrderNumber: string | null;
  importerOrderId?: string | null;
  enabled?: boolean;
};

export const useGetVehicleOrder = ({
  vehicleOrderNumber,
  importerOrderId,
  enabled = true,
}: UseGetVehicleOrderParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.VEHICLE_ORDER, vehicleOrderNumber],
    queryFn: async () => {
      const response = await LuppApi.get<VehicleOrderDto>(
        `${IMPORTER_API_BASE_PATH}/vehicle-orders/${vehicleOrderNumber}`,
        ...importerArgs
      );
      return mapVehicleOrderDto(response, importerOrderId);
    },
    enabled: enabled && !!vehicleOrderNumber,
  });
};

/*
|--------------------------------------------------------------------------
| Search deliveries  POST importer/X/v1/deliveries/searches
|--------------------------------------------------------------------------
*/

export type UseSearchDeliveriesParams = {
  enabled?: boolean;
};

export const useSearchDeliveries = ({ enabled = true }: UseSearchDeliveriesParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.DELIVERIES],
    queryFn: async () => {
      // x-importer SearchDeliveriesRequest only accepts: { models?, lineItemIds? }
      // Pagination is not supported — returns a flat list.
      const response = await LuppApi.post<Record<string, never>, DeliveryDtoItem[]>(
        `${IMPORTER_API_BASE_PATH}/deliveries/searches`,
        {},
        ...importerArgs
      );

      const items: DeliveryItem[] = (response ?? []).map((d) => ({
        orderIdentifier: d.lineItemId,
        importerOrderId: d.orderId,
        importerOrderNumber: d.orderId, // importerOrderNumber not returned; use orderId as fallback
        vin: d.vin ?? null,
        statusName: null,
      }));

      return {
        items,
        totalCount: items.length,
        totalPages: 1,
        currentPage: 0,
      };
    },
    placeholderData: keepPreviousData,
    enabled,
  });
};

/*
|--------------------------------------------------------------------------
| Get order deliveries  GET importer/X/v1/orders/{orderId}/deliveries
|--------------------------------------------------------------------------
*/

export type UseGetOrderDeliveriesParams = {
  orderId: string | null;
  enabled?: boolean;
};

export const useGetOrderDeliveries = ({ orderId, enabled = true }: UseGetOrderDeliveriesParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.ORDER_DELIVERIES, orderId],
    queryFn: async () => {
      const response = await LuppApi.get<OrderDelivery[]>(
        `${IMPORTER_API_BASE_PATH}/orders/${orderId}/deliveries`,
        ...importerArgs
      );
      return response ?? [];
    },
    enabled: enabled && !!orderId,
  });
};

/*
|--------------------------------------------------------------------------
| Download order confirmation (commercial invoice)  GET /X/v1/orders/{orderNumber}/confirmation
|--------------------------------------------------------------------------
*/

export const downloadOrderConfirmation = async (orderNumber: string): Promise<void> => {
  if (!orderNumber) {
    throw new Error('Order number is required');
  }

  const response = await LuppApi.get<{ orderNumber: string; fileId: string; fileName?: string }>(
    `${IMPORTER_API_BASE_PATH}/orders/${orderNumber}/confirmation`,
    ...importerArgs
  );

  if (!response?.fileId) {
    throw new Error(`No fileId returned for order confirmation: ${orderNumber}`);
  }

  const resolvedOrderNumber = response.orderNumber || orderNumber;
  const resolvedFileName = response.fileName || `Order-Confirmation-${resolvedOrderNumber}.pdf`;
  await downloadFileFromFilehub(
    response.fileId,
    resolvedFileName,
    FILEHUB_MODULE_NAME.ORDER_MANAGEMENT
  );
};

/*
|--------------------------------------------------------------------------
| Update delivery status  PATCH importer/X/v1/orders/{orderId}/deliveries/{deliveryId}/status
|--------------------------------------------------------------------------
*/

export type UpdateDeliveryStatusParams = {
  orderId: string;
  deliveryId: string;
  status: DeliveryStatus;
};

export const useUpdateDeliveryStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, deliveryId, status }: UpdateDeliveryStatusParams) => {
      const response = await LuppApi.patch<UpdateDeliveryStatusRequest, OrderDelivery>(
        `${IMPORTER_API_BASE_PATH}/orders/${orderId}/deliveries/${deliveryId}/status`,
        { status },
        ...importerArgs
      );
      return response;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [LUPP_QUERY_KEYS.ORDER_DELIVERIES, variables.orderId],
      });
    },
  });
};

/*
|--------------------------------------------------------------------------
| Confirm delivery pickup  POST importer/X/v1/orders/{orderId}/deliveries/{deliveryId}/pickup-confirmation
|--------------------------------------------------------------------------
*/

export type ConfirmPickupParams = { orderId: string; deliveryId: string } & ConfirmPickupPayload;

export const useConfirmDeliveryPickup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, deliveryId, ...payload }: ConfirmPickupParams) =>
      LuppApi.post<Omit<ConfirmPickupParams, 'orderId' | 'deliveryId'>, OrderDelivery>(
        `${IMPORTER_API_BASE_PATH}/orders/${orderId}/deliveries/${deliveryId}/pickup-confirmation`,
        payload,
        ...importerArgs
      ),
    onSuccess: (_data, { orderId }) => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.ORDER_DELIVERIES, orderId] });
    },
  });
};

/*
|--------------------------------------------------------------------------
| Get change request  GET importer/X/v1/orders/change-requests/{changeRequestId}
|--------------------------------------------------------------------------
*/

export type UseGetChangeRequestParams = {
  changeRequestId: string | null;
  enabled?: boolean;
};

export const useGetChangeRequest = ({
  changeRequestId,
  enabled = true,
}: UseGetChangeRequestParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.CHANGE_REQUEST, changeRequestId],
    queryFn: async () => {
      const response = await LuppApi.get<ChangeRequestDetail>(
        `${IMPORTER_API_BASE_PATH}/orders/change-requests/${changeRequestId}`,
        ...importerArgs
      );
      return response;
    },
    enabled: enabled && !!changeRequestId,
  });
};

const invalidateOrderMutationQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.IMPORTER_ORDERS] });
  queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.IMPORTER_ORDER_LINE_ITEMS] });
  queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.ORDER_NOTIFICATIONS] });
};

/*
|--------------------------------------------------------------------------
| Approve single vehicle order  PATCH importer/X/v1/orders/{orderNumber}/line-items/{orderLineNumber}/approve
|--------------------------------------------------------------------------
*/

export type ApproveVehicleOrderParams = {
  orderNumber: string;
  orderLineNumber: string;
};

export const useApproveVehicleOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderNumber, orderLineNumber }: ApproveVehicleOrderParams) => {
      return await LuppApi.patch<undefined, LineItemActionResultDto>(
        `${IMPORTER_API_BASE_PATH}/orders/${orderNumber}/line-items/${orderLineNumber}/approve`,
        undefined,
        ...importerArgs
      );
    },
    onSuccess: () => invalidateOrderMutationQueries(queryClient),
  });
};

/*
|--------------------------------------------------------------------------
| Reject single vehicle order  PATCH importer/X/v1/orders/{orderNumber}/line-items/{orderLineNumber}/reject
|--------------------------------------------------------------------------
*/

export type RejectVehicleOrderParams = {
  orderNumber: string;
  orderLineNumber: string;
  reason?: string;
};

export const useRejectVehicleOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderNumber, orderLineNumber, reason }: RejectVehicleOrderParams) => {
      // x-importer RejectLineItemRequest.reason — always send a value (even empty string)
      // LuppApi unwraps ApiResponsePayload — response IS LineItemActionResultDto
      return await LuppApi.patch<{ reason: string }, LineItemActionResultDto>(
        `${IMPORTER_API_BASE_PATH}/orders/${orderNumber}/line-items/${orderLineNumber}/reject`,
        { reason: reason ?? '' },
        ...importerArgs
      );
    },
    onSuccess: () => invalidateOrderMutationQueries(queryClient),
  });
};

/*
|--------------------------------------------------------------------------
| Bulk approve all vehicle orders  PATCH importer/X/v1/orders/{orderNumber}/approve-all
|--------------------------------------------------------------------------
*/

export type BulkApproveVehicleOrdersParams = {
  orderNumber: string;
};

export const useBulkApproveVehicleOrders = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderNumber }: BulkApproveVehicleOrdersParams) => {
      // LuppApi unwraps ApiResponsePayload — response IS BulkApproveResponseDto
      // Fields: totalRequested, approved, failed, results[]
      return await LuppApi.patch<undefined, BulkApproveResponseDto>(
        `${IMPORTER_API_BASE_PATH}/orders/${orderNumber}/approve-all`,
        undefined,
        ...importerArgs
      );
    },
    onSuccess: () => invalidateOrderMutationQueries(queryClient),
  });
};

/*
|--------------------------------------------------------------------------
| Submit cart  POST importer/X/v1/carts/{cartId}/submit
|--------------------------------------------------------------------------
*/

export type SubmitCartParams = {
  cartId: string;
  vehicles: VehicleOrderLineRequest[];
  notes?: string;
};

export const useSubmitCart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ cartId, vehicles, notes }: SubmitCartParams) => {
      const submitArgs = {
        axiosConfig: {
          ...(IMPORTER_BASE_URL ? { baseURL: IMPORTER_BASE_URL } : {}),
          headers: { ...(CART_TENANT ? { 'X-Tenant-Id': CART_TENANT } : {}) },
        },
      };
      return await LuppApi.post<
        { vehicles: VehicleOrderLineRequest[]; notes?: string },
        SubmitCartResult
      >(
        `${IMPORTER_API_BASE_PATH}/carts/${cartId}/submit`,
        { vehicles, ...(notes ? { notes } : {}) },
        submitArgs
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.IMPORTER_ORDERS] });
    },
  });
};

/*
|--------------------------------------------------------------------------
| Checkout cart  POST importer/X/v1/carts/{cartId}/checkout
|--------------------------------------------------------------------------
*/

export type UseCheckoutCartParams = {
  cartId: string;
};

/*
|--------------------------------------------------------------------------
| Get order notifications  GET importer/X/v1/orders/notifications
|--------------------------------------------------------------------------
*/

export const useGetOrderNotifications = ({
  enabled = true,
  importerOrderNumber,
}: { enabled?: boolean; importerOrderNumber?: string | null } = {}) => {
  const url = importerOrderNumber
    ? `${IMPORTER_API_BASE_PATH}/orders/notifications?importerOrderNumber=${importerOrderNumber}`
    : `${IMPORTER_API_BASE_PATH}/orders/notifications`;

  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.ORDER_NOTIFICATIONS, importerOrderNumber ?? null],
    queryFn: async () => {
      const response = await LuppApi.get<OrderNotificationsResult>(url, ...importerArgs);
      return response;
    },
    enabled,
  });
};

/*
|--------------------------------------------------------------------------
| Get baseline vs proposed comparison  GET importer/X/v1/orders/{orderNumber}/line-items/{orderLineNumber}/comparison
|--------------------------------------------------------------------------
*/

export type UseGetVehicleOrderComparisonParams = {
  orderNumber: string | null;
  orderLineNumber: string | null;
  enabled?: boolean;
};

export const useGetVehicleOrderComparison = ({
  orderNumber,
  orderLineNumber,
  enabled = true,
}: UseGetVehicleOrderComparisonParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.VEHICLE_ORDER_COMPARISON, orderNumber, orderLineNumber],
    queryFn: async () => {
      const response = await LuppApi.get<VehicleOrderComparison>(
        `${IMPORTER_API_BASE_PATH}/orders/${orderNumber}/line-items/${orderLineNumber}/comparison`,
        ...importerArgs
      );
      return response;
    },
    enabled: enabled && !!orderNumber && !!orderLineNumber,
  });
};

/*
|--------------------------------------------------------------------------
| Checkout cart  POST importer/X/v1/carts/{cartId}/checkout
|--------------------------------------------------------------------------
*/

export const useCheckoutCart = () => {
  return useMutation({
    mutationFn: async ({ cartId }: UseCheckoutCartParams) => {
      const checkoutArgs = {
        axiosConfig: {
          ...(IMPORTER_BASE_URL ? { baseURL: IMPORTER_BASE_URL } : {}),
          headers: { ...(CART_TENANT ? { 'X-Tenant-Id': CART_TENANT } : {}) },
        },
        // x-importer CartResponse is NOT wrapped in ApiResponsePayload — return as-is
        extractData: (res: unknown) => res,
      };
      return await LuppApi.post<Record<string, never>, CheckoutCartResult>(
        `${IMPORTER_API_BASE_PATH}/carts/${cartId}/checkout`,
        {},
        checkoutArgs
      );
    },
  });
};
